import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useContext } from 'react';
import { useSettlementQuery } from '@syf-api-library/settlement-eligibility';
import { ModalFooter } from 'syf-component-library/ui/patterns/SlideInModal';
import { FooterContainer } from 'syf-component-library/ui/patterns/SlideInModal/subcomponents';
import { Inset } from 'syf-component-library/ui/spacing';
import { useAccountActivity } from '../../../../api/accountActivity';
import { DATA_OBJECT, DATA_TYPE } from '../../../../const/analytics';
import { paymentTypes } from '../../../../const/paymentOptions';
import MainContext from '../../../../Context';
import { isPayLaterProduct, isRevolvingCreditProduct, isSecuredInstallmentLoanProduct, isSetpayLegacyProduct } from '../../../../helpers';
import useApiMachine from '../../../../hooks/machines/useApiMachine';
import useFormValues from '../../../../hooks/machines/useFormValues';
import usePaymentFlowMachine from '../../../../hooks/machines/usePaymentFlowMachine';
import { useDigitalCollections } from '../../../../hooks/useDigitalCollections';
import { t } from '../../../../strings/i18n';
import { MAPFlowTypes } from '../../../../typings/deepLink';
import IntroHeading from '../../../../ui/atoms/IntroHeading';
import PageLevelError from '../../../../ui/atoms/PageLevelError';
import { IntroHeadingInset, MessageBoxInset, PaymentModalWrapper } from '../../../../ui/atoms/styled';
import { useXForm, XForm } from '../../../../xform';
import CAPMessageBox from '../../CAPMessageBox';
import CAPNullFixedAmountMessageBox from '../../CAPNullFixedAmountMessageBox';
import SettlementMessageBox from '../../SettlementMessageBox';
import { getHasMatchingPayment } from '../helpers';
import { OneTimePaymentAmountSelect, PaymentDateSelect, PaymentMethodSelect, PaymentTypeSelect } from '../PaymentFormSubcomponents';
import PaymentStepper from '../PaymentFormSubcomponents/PaymentStepper';
const Form = () => {
    const { submit } = useXForm();
    const handleSubmit = (e) => {
        e.preventDefault();
        submit();
    };
    return (_jsxs("form", Object.assign({ onSubmit: handleSubmit }, { children: [_jsx(PaymentTypeSelect, { value: paymentTypes.ONCE }), _jsx(PaymentDateSelect, { id: "paymentDate" }), _jsx(OneTimePaymentAmountSelect, { id: "paymentAmount" }), _jsx(PaymentMethodSelect, { id: "paymentMethod" }), _jsx("input", { "aria-hidden": true, "data-testid": "submit-button", style: { display: 'none' }, type: "submit" })] })));
};
const OneTimePaymentForm = () => {
    var _a, _b;
    const { additionalProps, handleClose, featureFlags, apiConfig } = useContext(MainContext);
    const [{ data: activities }] = useAccountActivity();
    const [, oneTimePaymentActor, sendToOneTimeMachine] = usePaymentFlowMachine();
    const { paymentDate, paymentMethod } = useFormValues();
    const [apiMachine] = useApiMachine();
    const { isCustomerAssistancePlan, isSettlement } = useDigitalCollections();
    const { accountSummaryQueryResult, autopayStatusQueryResult } = apiMachine.context;
    const { data: accountSummary } = accountSummaryQueryResult;
    const { data: autopayStatus } = autopayStatusQueryResult;
    const { billing, product } = accountSummary;
    const fixedPaymentAmount = billing === null || billing === void 0 ? void 0 : billing.fixedPaymentAmount;
    const forbearanceEndDate = billing === null || billing === void 0 ? void 0 : billing.forbearanceEndDate;
    const activity = !isSecuredInstallmentLoanProduct(product.type) && activities.activity;
    const isAutopayEnrolled = autopayStatus === null || autopayStatus === void 0 ? void 0 : autopayStatus.enrollment;
    const hasMatchingPayment = !isSecuredInstallmentLoanProduct(product.type) &&
        getHasMatchingPayment(paymentDate, paymentMethod, activity);
    const { data: settlementEligibility } = useSettlementQuery({
        config: apiConfig.config,
        keyProps: apiConfig.keyProps,
        queryOptions: Object.assign(Object.assign({}, apiConfig.queryOptions), { enabled: isRevolvingCreditProduct(product.type) })
    });
    const isMultiPaymentFlow = ((_a = additionalProps === null || additionalProps === void 0 ? void 0 : additionalProps.flow) === null || _a === void 0 ? void 0 : _a.type) === 'MULTI_PAYMENT_FLOW' &&
        ((_b = settlementEligibility === null || settlementEligibility === void 0 ? void 0 : settlementEligibility.paymentArrangementPlans) === null || _b === void 0 ? void 0 : _b.length) > 1;
    const submitHandler = async () => {
        /*
         * Normally, the submit button would be nested as a child of the `<form />`.
         * But our UI has it outside of the form element so we have to use the native
         * machine API to send the submit event
         */
        oneTimePaymentActor.context.form.send({
            type: 'SUBMIT'
        });
    };
    const onSubmitHandler = () => {
        sendToOneTimeMachine({ type: 'NEXT' });
    };
    const handleCancel = () => {
        if (additionalProps.flow.type === MAPFlowTypes.MULTI_PAYMENT_FLOW &&
            featureFlags.isDigitalCollectionsEnabled) {
            handleClose();
        }
        else {
            sendToOneTimeMachine({ type: 'CANCEL' });
        }
    };
    const shouldOfferOneTimePaymentAndPayoff = isAutopayEnrolled &&
        (isSetpayLegacyProduct(product.type) || isPayLaterProduct(product.type));
    const getInstructionText = () => {
        if (shouldOfferOneTimePaymentAndPayoff) {
            return `${t('makePayment.makePaymentAutopayOnInstruction')}`;
        }
        if (isSettlement) {
            return `${t('makePayment.makePaymentSettlementInstruction')}`;
        }
        return `${t('makePayment.makePaymentInstruction')}`;
    };
    const renderCAPMessageBox = useCallback(() => {
        if (!isCustomerAssistancePlan)
            return null;
        if (!fixedPaymentAmount) {
            return _jsx(CAPNullFixedAmountMessageBox, {});
        }
        return (_jsx(CAPMessageBox, { featureFlags: featureFlags, forbearanceEndDate: forbearanceEndDate, paymentAmount: fixedPaymentAmount }));
    }, [
        featureFlags,
        fixedPaymentAmount,
        forbearanceEndDate,
        isCustomerAssistancePlan
    ]);
    return (_jsxs(_Fragment, { children: [!isMultiPaymentFlow && (_jsxs(_Fragment, { children: [_jsx(PaymentStepper, { step: "details" }), _jsxs(MessageBoxInset, Object.assign({ bottom: "small", top: "xlarge", vertical: "large" }, { children: [renderCAPMessageBox(), isSettlement && (_jsx(SettlementMessageBox, { shouldDisableStartAnalytics: oneTimePaymentActor.event.type === 'EDIT' }))] }))] })), _jsx(IntroHeadingInset, Object.assign({ bottom: "base", top: "xlarge", vertical: "large" }, { children: _jsxs(IntroHeading, { children: [getInstructionText(), " ", t('label.allFieldRequiredInstruction')] }) })), hasMatchingPayment && (_jsx(Inset, Object.assign({ bottom: "large", vertical: "large" }, { children: _jsx(PageLevelError, { errorMessage: t('errors.matchingPaymentDate') }) }))), _jsx(PaymentModalWrapper, Object.assign({ bottom: "xlarge", vertical: "large" }, { children: _jsx(XForm, Object.assign({ formMachine: oneTimePaymentActor.context.form, submit: submitHandler, onSubmit: onSubmitHandler }, { children: _jsx(Form, {}) })) })), _jsx(FooterContainer, { children: _jsx(ModalFooter, { dataCy: "payment-form-footer", dataPrimaryObject: DATA_OBJECT.BUTTON, dataPrimaryReason: "next", dataSecondaryObject: DATA_OBJECT.BUTTON, dataSecondaryReason: "cancel", dataType: DATA_TYPE.MAKE_A_PAYMENT, handlePrimaryButton: submitHandler, handleSecondaryButton: handleCancel, primaryText: t('button.next'), secondaryButtonType: "ghost", secondaryText: t('button.cancel') }) })] }));
};
export default OneTimePaymentForm;
